<template>
	<div>
		<ClientOnly>
			<template #fallback>
				<v-sheet class="pa-6 text-center">
					<v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
					<p class="text-h6 mt-4">Loading transfer details...</p>
				</v-sheet>
			</template>
			<suspense>
				<listing-content v-if="data" :listing="data" />
				<template #fallback>
					<v-sheet class="pa-6 text-center">
						<v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
						<p class="text-h6 mt-4">Loading transfer details...</p>
					</v-sheet>
				</template>
			</suspense>

			<!-- Show error message if data loading failed -->
			<v-alert v-if="!data && errorMessage" type="error" variant="tonal" class="ma-4">
				{{ errorMessage }}
				<div class="text-center mt-3">
					<v-btn color="primary" to="/" prepend-icon="mdi-home">Return to Home</v-btn>
				</div>
			</v-alert>
		</ClientOnly>
	</div>
</template>
<script setup>
import { ref, onMounted, onErrorCaptured } from 'vue';

const route = useRoute();
const store = useCurrencyStore();
const listingStore = useListingsStore();
const { currency } = storeToRefs(store);
const data = ref(null);
const errorMessage = ref('');
import { Destinations } from '~/data/Records';

// Handle errors during data fetching
onErrorCaptured((error) => {
	console.error('Error loading transfer data:', error);
	errorMessage.value = 'Sorry, we encountered an error loading this transfer. Please try again later.';
	return false; // Prevent error from propagating
});




// Attempt to get listing data
const listingData = listingStore.getListingsBySlug(route.params.slug);

if (!listingData) {
	throw createError({
		statusCode: 404,
		statusMessage: 'Transfer Not Found'
	});
}

data.value = listingData;


// Extract SEO data
const {
	code,
	slug,
	title,
	content: { seo_description = '', meta_keywords = [], featured_highlights = [] } = {},
	ratePlans,
	reviewCount,
	reviews,
	images,
	origin,
	destination,
	updatedAt,
	content
} = listingData;



// Find the correct slug to use
const useSlug = slug.find(i => i === route.params.slug);
const canonicalURL = slug.length > 1 ? slug[1] : slug[0];

// Generate keywords string
const keywordsString = meta_keywords && meta_keywords.length
	? `${title}, Puerto Princesa, El Nido, Sabang, Port barton, San Vicente tours, travel, Palawan, ${meta_keywords.join(', ')}`
	: `${title}, Puerto Princesa, El Nido, Sabang, Port barton, San Vicente tours, travel, Palawan`;

// Generate tags string
const tagsString = meta_keywords && meta_keywords.length
	? meta_keywords.join(',')
	: '';

// Enhanced SEO meta tags
useSeoMeta({
	title: `${title} | Online Booking - Diskubre Palawan`,
	ogTitle: `${title} | Online Booking - Diskubre Palawan`,
	description: seo_description,
	ogDescription: seo_description,
	ogImage: images.map((img) => `https://www.diskubrepalawan.com${img}`),
	ogSiteName: 'Diskubre Palawan',
	ogUrl: `https://www.diskubrepalawan.com/transfers/${useSlug}`,
	ogLogo: 'https://www.diskubrepalawan.com/apple-touch-icon.png',
	twitterTitle: `${title} | Diskubre Palawan`,
	twitterDescription: seo_description,
	twitterImage: images.map((img) => `https://www.diskubrepalawan.com${img}`),
	twitterCard: 'summary_large_image',
	htmlAttrs: {
		lang: 'en',
	},
	// Additional meta tags for improved SEO
	keywords: keywordsString,
	robots: 'index, follow',
	author: 'Diskubre Palawan',
	'theme-color': '#1976D2',
	// Open Graph additional tags
	'og:type': 'product',
	'og:price:amount': ratePlans[currency.value].base.toString(),
	'og:price:currency': currency.value,
	'og:availability': 'instock',
	// Additional meta tags
	'article:published_time': updatedAt,
	'article:modified_time': updatedAt,
	'article:section': 'Transfers',
	'article:tag': tagsString,
});

// Enhanced JSON-LD structured data
const jsonLdData = {
	"@context": "https://schema.org",
	"@type": "Product",
	"name": title,
	"description": seo_description,
	"url": `https://www.diskubrepalawan.com/transfers/${useSlug}`,
	"image": images.map((img) => `https://www.diskubrepalawan.com${img}`),
	"brand": {
		"@type": "Organization",
		"name": "Diskubre Palawan",
		"url": "https://www.diskubrepalawan.com"
	},
	"offers": {
		"@type": "Offer",
		"price": ratePlans[currency.value].base,
		"priceCurrency": currency.value,
		"availability": "https://schema.org/InStock",
		"validFrom": new Date().toISOString(),
		"url": `https://www.diskubrepalawan.com/transfers/${useSlug}`
	},
	"review": reviews?.map(review => ({
		"@type": "Review",
		"reviewRating": {
			"@type": "Rating",
			"ratingValue": review.reviewRating,
			"bestRating": "5"
		},
		"author": {
			"@type": "Person",
			"name": review.author
		},
		"reviewBody": review.reviewContent
	})) || [],
	"aggregateRating": reviews?.length > 0 ? {
		"@type": "AggregateRating",
		"ratingValue": reviews.reduce((acc, review) => acc + review.reviewRating, 0) / reviews.length,
		"bestRating": "5",
		"worstRating": "1",
		"reviewCount": reviewCount || reviews.length
	} : undefined,
	"productID": code,
	"category": "Transfer",
	"additionalProperty": [
		{
			"@type": "PropertyValue",
			"name": "Origin",
			"value": Destinations.find(dest => dest.code === origin)?.title || origin
		},
		{
			"@type": "PropertyValue",
			"name": "Destination",
			"value": Destinations.find(dest => dest.code === destination)?.title || destination
		},
		{
			"@type": "PropertyValue",
			"name": "Duration",
			"value": content.duration
		}
	]
};

// Add breadcrumb data to the schema
const breadcrumbData = {
	"@context": "https://schema.org",
	"@type": "BreadcrumbList",
	"itemListElement": [
		{
			"@type": "ListItem",
			"position": 1,
			"name": "Home",
			"item": "https://www.diskubrepalawan.com"
		},
		{
			"@type": "ListItem",
			"position": 2,
			"name": "Transfers",
			"item": "https://www.diskubrepalawan.com/transfers"
		},
		{
			"@type": "ListItem",
			"position": 3,
			"name": title,
			"item": `https://www.diskubrepalawan.com/transfers/${useSlug}`
		}
	]
};

useHead({
	script: [
		{
			key: 'jsonld-transfers',
			type: 'application/ld+json',
			children: JSON.stringify(jsonLdData),
		},
		{
			key: 'jsonld-breadcrumbs',
			type: 'application/ld+json',
			children: JSON.stringify(breadcrumbData),
		}
	],
	link: canonicalURL ? [
		{ rel: 'canonical', href: `https://www.diskubrepalawan.com/transfers/${canonicalURL}` }
	] : [],
});

</script>
